import { pathToRegexp } from 'path-to-regexp';
import type { ClientConfig } from './types';

interface MicrofrontendConfigClientOptions {
  removeFlaggedPaths?: boolean;
}

export class MicrofrontendConfigClient {
  applications: ClientConfig['applications'];
  pathCache: Record<string, string> = {};
  private readonly serialized: ClientConfig;

  constructor(config: ClientConfig, opts?: MicrofrontendConfigClientOptions) {
    this.serialized = config;
    if (opts?.removeFlaggedPaths) {
      for (const app of Object.values(config.applications)) {
        if (app.routing) {
          app.routing = app.routing.filter((match) => !match.flag);
        }
      }
    }
    this.applications = config.applications;
  }

  /**
   * Create a new `MicrofrontendConfigClient` from a JSON string.
   * Config must be passed in to remain framework agnostic
   */
  static fromEnv(
    config: string | undefined,
    opts?: MicrofrontendConfigClientOptions,
  ): MicrofrontendConfigClient {
    if (!config) {
      throw new Error(
        'Could not construct MicrofrontendConfigClient: configuration is empty or undefined. Did you set up your application with `withMicrofrontends`?',
      );
    }
    return new MicrofrontendConfigClient(
      JSON.parse(config) as ClientConfig,
      opts,
    );
  }

  isEqual(other: MicrofrontendConfigClient): boolean {
    return (
      JSON.stringify(this.applications) === JSON.stringify(other.applications)
    );
  }

  getApplicationNameForPath(path: string): string | null {
    if (!path.startsWith('/')) {
      throw new Error(`Path must start with a /`);
    }

    if (this.pathCache[path]) {
      return this.pathCache[path];
    }

    const pathname = new URL(path, 'https://example.com').pathname;
    for (const [name, application] of Object.entries(this.applications)) {
      if (application.routing) {
        for (const group of application.routing) {
          for (const childPath of group.paths) {
            const regexp = pathToRegexp(childPath);
            if (regexp.test(pathname)) {
              this.pathCache[path] = name;
              return name;
            }
          }
        }
      }
    }
    const defaultApplication = Object.entries(this.applications).find(
      ([, application]) => application.default,
    );
    if (!defaultApplication) {
      return null;
    }

    this.pathCache[path] = defaultApplication[0];
    return defaultApplication[0];
  }

  serialize(): ClientConfig {
    return this.serialized;
  }
}
